import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  LocalOffer,
  Store,
  TrendingUp,
  Assessment,
} from '@mui/icons-material';
import Layout from '../../components/Layout';
import apiService from '../../services/api';

interface Stats {
  total_cupones: number;
  cupones_activos: number;
  total_comercios: number;
  comercios_activos: number;
  redenciones_hoy: number;
  redenciones_mes: number;
}

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await apiService.getDashboard();
      
      if (response.success) {
        // Simular datos por ahora ya que el backend devuelve estructura básica
        setStats({
          total_cupones: 150,
          cupones_activos: 120,
          total_comercios: 25,
          comercios_activos: 22,
          redenciones_hoy: 8,
          redenciones_mes: 185,
        });
      }
    } catch (error: any) {
      console.error('Error cargando dashboard:', error);
      setError('Error cargando datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box>
        <Typography variant="h4" gutterBottom>
          Dashboard Administrativo
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Estadísticas principales */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Card sx={{ minWidth: 280, flex: 1 }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <LocalOffer color="primary" sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Total Cupones
                  </Typography>
                  <Typography variant="h5">
                    {stats?.total_cupones || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ minWidth: 280, flex: 1 }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Store color="secondary" sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Comercios Activos
                  </Typography>
                  <Typography variant="h5">
                    {stats?.comercios_activos || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ minWidth: 280, flex: 1 }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUp color="success" sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Redenciones Hoy
                  </Typography>
                  <Typography variant="h5">
                    {stats?.redenciones_hoy || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ minWidth: 280, flex: 1 }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Assessment color="info" sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Redenciones Mes
                  </Typography>
                  <Typography variant="h5">
                    {stats?.redenciones_mes || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Resumen detallado */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Paper sx={{ p: 2, flex: 1, minWidth: 300 }}>
            <Typography variant="h6" gutterBottom>
              Resumen de Cupones
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography color="text.secondary">Total:</Typography>
                <Typography>{stats?.total_cupones || 0}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography color="text.secondary">Activos:</Typography>
                <Typography color="success.main">{stats?.cupones_activos || 0}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography color="text.secondary">Inactivos:</Typography>
                <Typography color="error.main">
                  {(stats?.total_cupones || 0) - (stats?.cupones_activos || 0)}
                </Typography>
              </Box>
            </Box>
          </Paper>

          <Paper sx={{ p: 2, flex: 1, minWidth: 300 }}>
            <Typography variant="h6" gutterBottom>
              Resumen de Comercios
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography color="text.secondary">Total:</Typography>
                <Typography>{stats?.total_comercios || 0}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography color="text.secondary">Activos:</Typography>
                <Typography color="success.main">{stats?.comercios_activos || 0}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography color="text.secondary">Inactivos:</Typography>
                <Typography color="error.main">
                  {(stats?.total_comercios || 0) - (stats?.comercios_activos || 0)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Acciones rápidas */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Acciones Rápidas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Utiliza el menú lateral para gestionar cupones, comercios y generar reportes.
            También puedes usar el escáner QR para validar cupones en tiempo real.
          </Typography>
        </Paper>
      </Box>
    </Layout>
  );
};

export default AdminDashboard;
